/**
 * Obstacle Type Definitions
 * System for static obstacles like platforms, walls, etc.
 */

import obstacleConfigData from '@/config/obstacleConfig.json';

/**
 * Obstacle configuration interface
 */
export interface ObstacleConfig {
  name: string;
  sprite: string;
  assetPath: string;
  collisionType: 'solid' | 'passthrough'; // solid = blocks everything, passthrough = only blocks from top
  blockProjectiles: boolean; // Whether projectiles stop at this obstacle
  ballBounce: boolean; // Whether balls bounce off this obstacle
  /**
   * Visual rendering mode (defaults to 'frame'):
   *   - 'frame'  → procedural Pang-style colored frame + sand fill (most obstacles)
   *   - 'sprite' → render the actual asset image at obstacle dimensions, no frame
   * Collision still uses the bounds rect either way.
   */
  renderMode?: 'frame' | 'sprite';
}

/**
 * Obstacle configuration map
 */
export interface ObstacleConfigMap {
  [key: string]: ObstacleConfig;
}

/**
 * Load obstacle configurations from JSON
 */
const obstacleConfigs: ObstacleConfigMap = obstacleConfigData.obstacles as ObstacleConfigMap;

/**
 * Valid obstacle type names
 */
export type ObstacleTypeName = keyof typeof obstacleConfigs;

/**
 * Get all obstacle type names
 */
export function getAllObstacleTypes(): ObstacleTypeName[] {
  return Object.keys(obstacleConfigs) as ObstacleTypeName[];
}

/**
 * Get obstacle configuration by type name
 */
export function getObstacleConfig(typeName: ObstacleTypeName): ObstacleConfig {
  const config = obstacleConfigs[typeName];
  if (!config) {
    console.warn(`⚠️ Obstacle type '${typeName}' not found, using default`);
    return obstacleConfigs['rusty_platform'];
  }
  return config;
}

/**
 * Get all obstacle asset paths for preloading
 */
export function getAllObstacleAssets(): { sprite: string; path: string }[] {
  return Object.values(obstacleConfigs).map(config => ({
    sprite: config.sprite,
    path: config.assetPath
  }));
}
